// The form builder (Settings › Job forms; forms.list / define / update / retire / restore): a form
// as the office edits it, plain text, turned into the action's input or the one thing to fix first.

/** A question's kind, in the office's words. */
export const FIELD_KINDS = [
  ['text', 'Text'],
  ['number', 'Number'],
  ['boolean', 'Yes / No'],
  ['choice', 'Pick one'],
  ['date', 'Date'],
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number][0];

/**
 * The kinds the builder offers. Pick one and Date were held back until the iPad drew them; since
 * 14d1860 (flowology-tech's Work.tsx: pick-one, date and help) it does, so all five are offered.
 */
export const IPAD_HAS_PICK_AND_DATE = true;
export const offeredKinds = (ipadHasPickAndDate = IPAD_HAS_PICK_AND_DATE) =>
  FIELD_KINDS.filter(([k]) => ipadHasPickAndDate || (k !== 'choice' && k !== 'date'));

export interface FormFieldRow {
  key: string;
  label: string;
  type: string;
  required: boolean;
  help?: string;
  options?: string[];
}
export interface JobForm {
  id: string;
  key: string;
  name: string;
  requiredOnCompletion: boolean;
  /** BL-058: a required form is filled to complete the job, or on arrival, before the work starts. */
  requiredAt: RequiredAt;
  businessUnitId: string | null;
  jobTypeId: string | null;
  /** BL-058: only for this kind of customer (null: any). */
  customerKind: CustomerKind | null;
  /** BL-058: only when one of these price book items is on the job's invoice (none: any job). */
  items: Picked[];
  /** Q12, BL-027: only someone holding `licence`, valid that day, fills it in; never the office for them. */
  licensedSigner: boolean;
  licence: Picked | null;
  active: boolean;
  rowVersion: number;
  versionId: string | null;
  version: number | null;
  fields: FormFieldRow[];
  submissions: number;
  /** Of those, the company's own, filed in ServiceTitan on no job (#81); absent from an older server. */
  offJob?: number;
  /** Came over from ServiceTitan: changed and retired there while the company mirrors it (BL-088). */
  fromServiceTitan?: boolean;
}

/** Whether the office changes, retires or restores it here: its own always, ServiceTitan's once live. */
export const formEditable = (f: Pick<JobForm, 'fromServiceTitan'>, mirroring: boolean): boolean =>
  !(mirroring && f.fromServiceTitan === true);

/** One of a form's submissions on no job (forms.offJob). */
export interface OffJobSubmission {
  id: string;
  submittedAt: string;
  by: string | null;
  answers: { question: string; answer: string }[];
}

/** How many of a form's submissions on no job the sheet reads at a time (list caps M10). */
export const OFF_JOB_PAGE = 100;

/** forms.offJob's input for a form, a page at a time. */
export const offJobInput = (formId: string) => ({ formId, limit: OFF_JOB_PAGE });

/** forms.offJob's answer, a page at a time: `total` counts them all. */
export interface OffJobPage {
  submissions: OffJobSubmission[];
  next: string | null;
  total: number;
}

/** "Filled in 3 times on no job (ServiceTitan's)", or nothing. */
export function offJobWords(f: Pick<JobForm, 'offJob'>): string | null {
  const n = f.offJob ?? 0;
  return n ? `Filled in ${n} time${n === 1 ? '' : 's'} on no job (ServiceTitan’s)` : null;
}

export type RequiredAt = 'completion' | 'arrival';
export type CustomerKind = 'residential' | 'commercial';
export interface Picked {
  id: string;
  name: string;
}

/** When a required form is filled in, as the sheet offers it. */
export const WHEN_CHOICES = [
  ['completion', 'Any time before the job is completed'],
  ['arrival', 'On arrival, before the work starts'],
] as const;

export const CUSTOMER_CHOICES = [
  ['', 'Every customer'],
  ['residential', 'Residential customers'],
  ['commercial', 'Commercial customers'],
] as const;

/** The items that can call for a form: as many as the server takes. */
export const MAX_FORM_ITEMS = 20;

/** Completed work: required on every job, never retired (the server says so too). */
export const COMPLETED_WORK_KEY = 'completed_work';

export interface QuestionForm {
  /** The answers' key: kept for a question the form already had; made from the label for a new one. */
  key: string | null;
  label: string;
  type: FieldKind;
  required: boolean;
  help: string;
  /** Pick one: one answer per line. */
  options: string;
}
export interface FormForm {
  name: string;
  requiredOnCompletion: boolean;
  requiredAt: RequiredAt;
  businessUnitId: string;
  jobTypeId: string;
  customerKind: '' | CustomerKind;
  items: Picked[];
  licensedSigner: boolean;
  licenceKindId: string;
  questions: QuestionForm[];
}

export const emptyQuestion = (): QuestionForm => ({
  key: null,
  label: '',
  type: 'text',
  required: false,
  help: '',
  options: '',
});
export const emptyForm = (): FormForm => ({
  name: '',
  requiredOnCompletion: false,
  requiredAt: 'completion',
  businessUnitId: '',
  jobTypeId: '',
  customerKind: '',
  items: [],
  licensedSigner: false,
  licenceKindId: '',
  questions: [emptyQuestion()],
});
export const formForm = (f: JobForm): FormForm => ({
  name: f.name,
  requiredOnCompletion: f.requiredOnCompletion,
  requiredAt: f.requiredAt,
  businessUnitId: f.businessUnitId ?? '',
  jobTypeId: f.jobTypeId ?? '',
  customerKind: f.customerKind ?? '',
  items: f.items,
  licensedSigner: f.licensedSigner,
  licenceKindId: f.licence?.id ?? '',
  questions: f.fields.map((q) => ({
    key: q.key,
    label: q.label,
    type: (FIELD_KINDS.some(([k]) => k === q.type) ? q.type : 'text') as FieldKind,
    required: q.required,
    help: q.help ?? '',
    options: (q.options ?? []).join('\n'),
  })),
});

/** A new question's key from its label, unique in the form: "Pipe material" → pipe_material (_2…). */
export function keyFromLabel(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'q_$1')
      .slice(0, 36) || 'question';
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}_${n}`)) return `${base}_${n}`;
}

const lines = (s: string) =>
  s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);

type Built = { input: Record<string, unknown> } | { error: string };

/** The same questions whatever order each one's keys arrive in (the server's copy reads back sorted). */
const sameFields = (a: readonly FormFieldRow[], b: readonly FormFieldRow[]) => {
  const canon = (fs: readonly FormFieldRow[]) =>
    JSON.stringify(
      fs.map((f) => Object.fromEntries(Object.entries(f).sort(([x], [y]) => x.localeCompare(y)))),
    );
  return canon(a) === canon(b);
};

/** The questions as the server takes them, or the first thing to fix (said about the question by number). */
export function questionsInput(qs: readonly QuestionForm[]): { fields: FormFieldRow[] } | { error: string } {
  if (!qs.length) return { error: 'Add at least one question.' };
  const taken = new Set(qs.flatMap((q) => (q.key ? [q.key] : [])));
  const fields: FormFieldRow[] = [];
  for (const [i, q] of qs.entries()) {
    const n = `Question ${i + 1}`;
    const label = q.label.trim();
    if (!label) return { error: `${n} needs its wording.` };
    let key = q.key;
    if (!key) {
      key = keyFromLabel(label, taken);
      taken.add(key);
    }
    const f: FormFieldRow = { key, label, type: q.type, required: q.required };
    if (q.help.trim()) f.help = q.help.trim();
    if (q.type === 'choice') {
      const opts = lines(q.options);
      if (new Set(opts).size !== opts.length) return { error: `${n} lists an answer twice.` };
      if (opts.length < 2) return { error: `${n} needs at least two answers to pick from, one per line.` };
      f.options = opts;
    }
    fields.push(f);
  }
  return { fields };
}

/** forms.define for a new form; forms.update with only what changed for one that exists. */
export function formInput(was: JobForm | null, f: FormForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give the form a name.' };
  const q = questionsInput(f.questions);
  if ('error' in q) return q;
  if (f.items.length > MAX_FORM_ITEMS) return { error: `A form can name up to ${MAX_FORM_ITEMS} items.` };
  // the server's words (forms.define): the items are added during the work, after arrival
  if (f.requiredOnCompletion && f.requiredAt === 'arrival' && f.items.length)
    return {
      error:
        'A form asked for on arrival can’t wait for an item on the invoice: the invoice is usually empty then. Choose “before the job is completed”, or drop the items.',
    };
  const where = {
    businessUnitId: f.businessUnitId || null,
    jobTypeId: f.jobTypeId || null,
    customerKind: f.customerKind || null,
  };
  // the server's words (forms.define, BL-027): a licensed-signer form names its licence
  if (f.licensedSigner && !f.licenceKindId)
    return { error: 'Choose which licence the person filling in this form must hold.' };
  const licenceKindId = f.licensedSigner ? f.licenceKindId : null;
  const itemIds = f.items.map((i) => i.id);
  const { requiredOnCompletion, requiredAt } = f;
  if (!was)
    return {
      input: {
        name,
        requiredOnCompletion,
        requiredAt,
        ...where,
        itemIds,
        ...(f.licensedSigner ? { licensedSigner: true, licenceKindId } : {}),
        fields: q.fields,
      },
    };
  const changed: Record<string, unknown> = {};
  if (name !== was.name) changed.name = name;
  if (requiredOnCompletion !== was.requiredOnCompletion) changed.requiredOnCompletion = requiredOnCompletion;
  if (requiredAt !== was.requiredAt) changed.requiredAt = requiredAt;
  if (where.businessUnitId !== was.businessUnitId) changed.businessUnitId = where.businessUnitId;
  if (where.jobTypeId !== was.jobTypeId) changed.jobTypeId = where.jobTypeId;
  if (where.customerKind !== was.customerKind) changed.customerKind = where.customerKind;
  if (itemIds.join() !== was.items.map((i) => i.id).join()) changed.itemIds = itemIds;
  if (f.licensedSigner !== was.licensedSigner) changed.licensedSigner = f.licensedSigner;
  if (f.licensedSigner && licenceKindId !== (was.licence?.id ?? null)) changed.licenceKindId = licenceKindId;
  if (!sameFields(q.fields, was.fields)) changed.fields = q.fields;
  if (!Object.keys(changed).length) return { error: 'Nothing has changed yet.' };
  return { input: { id: was.id, rowVersion: was.rowVersion, ...changed } };
}

/** Whether saving would make a new version: the questions changed on a form that exists. */
export function makesNewVersion(was: JobForm | null, f: FormForm): boolean {
  if (!was) return false;
  const q = questionsInput(f.questions);
  return !('error' in q) && !sameFields(q.fields, was.fields);
}

/**
 * Where a form is asked for, in words: "Every job", "Service · Leak repair", "Commercial customers",
 * "When the invoice has Backflow test or Test kit".
 */
export function formScope(
  f: Pick<JobForm, 'businessUnitId' | 'jobTypeId'> & Partial<Pick<JobForm, 'customerKind' | 'items'>>,
  units: readonly { id: string; name: string }[],
  types: readonly { id: string; name: string }[],
): string {
  const u = f.businessUnitId ? (units.find((x) => x.id === f.businessUnitId)?.name ?? 'A department') : null;
  const t = f.jobTypeId ? (types.find((x) => x.id === f.jobTypeId)?.name ?? 'A job type') : null;
  const k = f.customerKind ? CUSTOMER_CHOICES.find(([v]) => v === f.customerKind)![1] : null;
  const names = (f.items ?? []).map((i) => i.name);
  const items = names.length
    ? `When the invoice has ${names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0]}`
    : null;
  return [u, t, k, items].filter(Boolean).join(' · ') || 'Every job';
}

/** The list's chip for a required form: "Required on arrival" or "Required to complete". */
export function requiredChip(f: Pick<JobForm, 'requiredOnCompletion' | 'requiredAt'>): string | null {
  if (!f.requiredOnCompletion) return null;
  return f.requiredAt === 'arrival' ? 'Required on arrival' : 'Required to complete';
}

/** Moves a question up (-1) or down (+1); the list as it was when it can't move further. */
export function moveQuestion<T>(qs: readonly T[], i: number, by: -1 | 1): T[] {
  const j = i + by;
  if (j < 0 || j >= qs.length) return [...qs];
  const out = [...qs];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}
